import moment from "moment";

/**
 * Utility functions for adjusting SmartOffer launch times
 */
export class SmartOfferTimeUtils {

    // Constants
    static SECONDS_PER_DAY = 86400;
    static DATE_FORMAT = 'DD/MM/YYYY HH:mm [UTC]';

    // Add validation helpers
    static _isValidNumber(value) {
        return typeof value === 'number' && Number.isFinite(value);
    }

    static _isValidTimestamp(value) {
        return this._isValidNumber(value) && value > 0;
    }

    static _sanitizeTimestamp(value, fallback = 0) {
        return this._isValidTimestamp(value) ? value : fallback;
    }

    /**
     * Helper to apply an advanced setting value if it's provided (not null/undefined)
     *
     * @param {Object} target - Target object to update
     * @param {string} property - Property name to set
     * @param {*} value - Value to set (only applied if not null/undefined)
     * @private
     */
    static _applyAdvancedSetting(target, property, value) {
        if (value !== undefined && value !== null) {
            target[property] = value;
        }
    }

    /**
     * Validate and sanitize advanced settings
     *
     * @param {Object} advancedSettings
     * @returns {Object} sanitized settings containing only valid numeric fields
     * @private
     */
    static _validateAdvancedSettings(advancedSettings = {}) {
        const safe = {};
        const { resetTime, cooldown, quantity } = advancedSettings || {};

        if (this._isValidNumber(resetTime)) {
            safe.resetTime = resetTime;
        }
        if (this._isValidNumber(cooldown) && cooldown >= 0) {
            safe.cooldown = cooldown;
        }
        if (this._isValidNumber(quantity) && quantity >= 0) {
            safe.quantity = quantity;
        }
        return safe;
    }

    /**
     * Apply all advanced settings to an offer clone
     *
     * @param {SmartOffer} offerClone - Cloned offer to update
     * @param {Object} advancedSettings - Settings object
     * @private
     */
    static _applyAdvancedSettings(offerClone, advancedSettings) {
        const safe = this._validateAdvancedSettings(advancedSettings);
        this._applyAdvancedSetting(offerClone, 'resetTime', safe.resetTime);
        this._applyAdvancedSetting(offerClone, 'cooldown', safe.cooldown);
        this._applyAdvancedSetting(offerClone, 'quantity', safe.quantity);
    }

    /**
     * Get the earliest launch time from a list of smart offers
     * This serves as the reference point for smart offset calculations
     *
     * @param {Array} offers - Array of SmartOffer objects
     * @returns {number} Unix timestamp of earliest launch time, or 0 if none found
     */
    static getReferenceDate(offers) {
        const launchTimes = [];

        (Array.isArray(offers) ? offers : []).forEach(offer => {
            if (offer.guardTime && offer.guardTime.rules) {
                offer.guardTime.rules.forEach(rule => {
                    const t = this._sanitizeTimestamp(rule?.launchTime, 0);
                    if (t > 0) {
                        launchTimes.push(t);
                    }
                });
            }
        });

        return launchTimes.length > 0 ? Math.min(...launchTimes) : 0;
    }

    /**
     * Calculate offset in seconds between two unix timestamps
     *
     * @param {number} newDate - New base date (unix timestamp)
     * @param {number} referenceDate - Reference date (unix timestamp)
     * @returns {number} Offset in seconds
     */
    static calculateOffset(newDate, referenceDate) {
        return newDate - referenceDate;
    }

    /**
     * Calculate offset in days between two unix timestamps
     *
     * @param {number} newDate - New base date (unix timestamp)
     * @param {number} referenceDate - Reference date (unix timestamp)
     * @returns {number} Offset in days (rounded)
     */
    static calculateOffsetDays(newDate, referenceDate) {
        const offsetSeconds = this.calculateOffset(newDate, referenceDate);
        return Math.round(offsetSeconds / this.SECONDS_PER_DAY);
    }

    /**
     * Apply smart offset to a single offer (maintains relative timing)
     *
     * @param {SmartOffer} offer - SmartOffer object to adjust
     * @param {number} offset - Offset in seconds
     * @param {Object} advancedSettings - Optional advanced settings (resetTime, cooldown, quantity)
     * @returns {SmartOffer} Cloned offer with adjusted times
     */
    static applySmartOffset(offer, offset, advancedSettings = {}) {
        const offerClone = offer.clone();

        // Adjust all guard time rules
        if (offerClone.guardTime && offerClone.guardTime.rules) {
            offerClone.guardTime.rules.forEach(rule => {
                const t = this._sanitizeTimestamp(rule.launchTime, 0);
                if (t > 0) {
                    rule.launchTime = t + offset;
                }
            });
        }

        // Apply advanced settings if provided
        this._applyAdvancedSettings(offerClone, advancedSettings);

        return offerClone;
    }

    /**
     * Apply smart offset to multiple offers
     *
     * @param {Array} offers - Array of SmartOffer objects
     * @param {number} newBaseDate - New base date (unix timestamp)
     * @param {Object} advancedSettings - Optional advanced settings
     * @param {Object} options - Optional flags { strict: boolean }
     * @returns {Array} Array of cloned offers with adjusted times
     */
    static applySmartOffsetToMany(offers, newBaseDate, advancedSettings = {}, options = {}) {
        const { strict = true } = options || {};
        const referenceDate = this.getReferenceDate(offers);
        if (referenceDate === 0) {
            if (strict) {
                throw new Error("No valid launch times found in selected offers");
            }
            // Non-throwing behavior: clone offers and only apply advanced settings
            // Log warning to make this behavior explicit
            console.warn(
                "[SmartOfferTimeUtils] No valid launch times found in offers. Time adjustments skipped. Only advanced settings will be applied."
            );
            return (Array.isArray(offers) ? offers : []).map(o => {
                const clone = o.clone();
                this._applyAdvancedSettings(clone, advancedSettings);
                return clone;
            });
        }

        const offset = this.calculateOffset(newBaseDate, referenceDate);

        return (Array.isArray(offers) ? offers : []).map(offer =>
            this.applySmartOffset(offer, offset, advancedSettings)
        );
    }

    /**
     * Apply manual date adjustments to offers
     *
     * Manual mode preserves relative timing between multiple guardTime rules by applying
     * a calculated offset to all rules, similar to smart mode but on a per-offer basis.
     *
     * The first guardTime rule (index 0) is treated as the baseline/primary launch time.
     * The offset is computed from this baseline rule and applied to all rules to maintain
     * their relative spacing.
     *
     * @param {Array} offerDateMap - Array of {offer, newLaunchTime} objects
     * @param {Object} advancedSettings - Optional advanced settings
     * @returns {Array} Array of cloned offers with adjusted times
     */
    static applyManualAdjustments(offerDateMap, advancedSettings = {}) {
        return (Array.isArray(offerDateMap) ? offerDateMap : []).map(({offer, newLaunchTime}) => {
            const offerClone = offer.clone();

            // Manual mode adjusts all rules by offset to preserve relative timing
            // The first rule (index 0) is assumed to be the baseline/primary launch time
            if (offerClone.guardTime && offerClone.guardTime.rules && offerClone.guardTime.rules.length > 0) {
                const originalFirstLaunchTime = this._sanitizeTimestamp(offerClone.guardTime.rules?.[0]?.launchTime, 0);
                const newBase = this._sanitizeTimestamp(newLaunchTime, 0);

                if (originalFirstLaunchTime > 0 && newBase > 0) {
                    // Compute offset from the baseline (first) rule
                    const offset = newBase - originalFirstLaunchTime;

                    // Apply the same offset to all rules to maintain relative timing
                    offerClone.guardTime.rules.forEach(rule => {
                        const t = this._sanitizeTimestamp(rule.launchTime, 0);
                        if (t > 0) {
                            rule.launchTime = t + offset;
                        }
                    });
                } else if (newBase > 0) {
                    // Fallback: Only set rules lacking valid times; keep valid ones unchanged
                    offerClone.guardTime.rules.forEach(rule => {
                        const t = this._sanitizeTimestamp(rule.launchTime, 0);
                        rule.launchTime = t > 0 ? t : newBase;
                    });
                }
            }

            // Apply advanced settings if provided
            this._applyAdvancedSettings(offerClone, advancedSettings);

            return offerClone;
        });
    }

    /**
     * Get the first launch time from an offer
     *
     * @param {SmartOffer} offer - SmartOffer object
     * @returns {number} Unix timestamp of first launch time, or 0 if none found
     */
    static getFirstLaunchTime(offer) {
        if (offer.guardTime && offer.guardTime.rules && offer.guardTime.rules.length > 0) {
            return this._sanitizeTimestamp(offer.guardTime.rules[0].launchTime);
        }
        return 0;
    }

    /**
     * Format unix timestamp to human-readable date
     *
     * @param {number} timestamp - Unix timestamp
     * @returns {string} Formatted date string (DD/MM/YYYY HH:mm UTC)
     */
    static formatDate(timestamp) {
        if (!timestamp || timestamp === 0) {
            return "Not set";
        }
        return moment.unix(timestamp).utc().format(this.DATE_FORMAT);
    }

    /**
     * Format difference between two dates
     *
     * @param {number} oldDate - Old unix timestamp
     * @param {number} newDate - New unix timestamp
     * @returns {string} Formatted difference (e.g., "+365 days")
     */
    static formatDateDiff(oldDate, newDate) {
        const diffSeconds = newDate - oldDate;
        const diffDays = Math.round(diffSeconds / this.SECONDS_PER_DAY);

        if (diffDays === 0) {
            return "No change";
        } else if (diffDays > 0) {
            return `+${diffDays} days`;
        } else {
            return `${diffDays} days`;
        }
    }

    /**
     * Generate preview data for time adjustments
     *
     * @param {Array} offers - Original offers
     * @param {Array} adjustedOffers - Adjusted offers
     * @returns {Array} Preview data with old/new dates and diff
     */
    static generatePreviewData(offers = [], adjustedOffers = []) {
        const offersArray = offers || [];
        const adjustedArray = adjustedOffers || [];

        // Prefer matching by uid to avoid order-mismatch issues
        const adjustedByUid = new Map(
            adjustedArray
                .filter(o => o && o.uid !== null && o.uid !== undefined)
                .map(o => [o.uid, o])
        );

        // Only trust index-based fallback when arrays appear aligned
        const canUseIndexFallback = offersArray.length === adjustedArray.length;

        return offersArray.map((offer, index) => {
            let adjustedOffer = offer && adjustedByUid.get(offer.uid);

            if (!adjustedOffer) {
                if (!canUseIndexFallback) {
                    // Arrays misaligned: avoid unsafe index-based association
                    console.warn(
                        "[SmartOfferTimeUtils] Unable to reliably match adjusted offer for index",
                        index,
                        "- offers and adjustedOffers appear misaligned. Using original offer data only."
                    );
                } else if (index < adjustedArray.length) {
                    // Arrays aligned: safe to use index fallback
                    adjustedOffer = adjustedArray[index];
                }
            }

            const oldTimeRaw = offer ? this.getFirstLaunchTime(offer) : 0;
            const newTimeRaw = adjustedOffer ? this.getFirstLaunchTime(adjustedOffer) : oldTimeRaw;

            const oldTime = this._sanitizeTimestamp(oldTimeRaw, 0);
            const newTime = this._isValidTimestamp(newTimeRaw) ? newTimeRaw : oldTime;

            const changed = !!adjustedOffer && newTime !== oldTime;

            return {
                uid: offer?.uid,
                name: offer?.name || offer?.key || offer?.uid || "Unnamed offer",
                oldDate: this.formatDate(oldTime),
                newDate: this.formatDate(newTime),
                diff: changed ? this.formatDateDiff(oldTime, newTime) : "No change",
                oldTimestamp: oldTime,
                newTimestamp: newTime
            };
        });
    }

    /**
     * Check if an offer has valid guardTime rules with launch times
     *
     * @param {SmartOffer} offer - SmartOffer object to check
     * @returns {boolean} True if offer has valid launch times
     */
    static hasValidLaunchTime(offer) {
        return offer.guardTime &&
               offer.guardTime.rules &&
               offer.guardTime.rules.length > 0 &&
               offer.guardTime.rules.some(rule => this._isValidTimestamp(rule.launchTime));
    }

    /**
     * Validate that offers array is not empty
     * Note: Individual offer validation should be done via hasValidLaunchTime() before calling this
     *
     * @param {Array} offers - Array of SmartOffer objects
     * @returns {Object} {valid: boolean, errors: Array}
     */
    static validateOffers(offers) {
        const errors = [];

        if (!offers || offers.length === 0) {
            errors.push("No offers selected");
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }
}
